/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * Creatable select: pick an existing option, or type a new name and create it
 * inline via onCreate (Enter or the "+ Create" row). Used wherever the user
 * builds a SKU (sizes / flavors / pack sizes) — System Settings → Product SKUs
 * and the shared Product Spec editor.
 *
 * onCreate(name) must return the created record ({ id, name, ... }).
 */

import { useRef, useState } from "react";
import { useClickOutside } from "../hooks/useClickOutside.js";

export default function CreatableSelect({ options, value, onChange, onCreate, placeholder = "Select or type…" }) {
  const [inputVal, setInputVal] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const selectedName = options.find((o) => String(o.id) === String(value))?.name ?? "";
  const trimmed = inputVal.trim();
  const filtered = options.filter((o) => o.name.toLowerCase().includes(trimmed.toLowerCase()));
  const exactMatch = options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase());
  const canCreate = trimmed.length > 0 && !exactMatch;

  function select(id) {
    onChange(id);
    setOpen(false);
    setInputVal("");
    setCreateErr(null);
  }

  async function create() {
    if (!trimmed) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const item = await onCreate(trimmed);
      onChange(item.id);
      setOpen(false);
      setInputVal("");
    } catch (e) {
      setCreateErr(e.message);
    } finally {
      setCreating(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && canCreate) { e.preventDefault(); create(); }
    if (e.key === "Escape") setOpen(false);
  }

  return (
    <div ref={ref} className="cs-wrap">
      <div className="cs-control" onClick={() => setOpen(true)}>
        {!open && selectedName ? (
          <span className="cs-value">{selectedName}</span>
        ) : (
          <input
            className="cs-input"
            value={inputVal}
            onChange={(e) => { setInputVal(e.target.value); setCreateErr(null); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={open ? "Type to search or create…" : placeholder}
            autoFocus={open}
          />
        )}
        <svg className="cs-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && (
        <div className="cs-menu">
          {filtered.length === 0 && !canCreate && <div className="cs-empty">No matches</div>}
          {filtered.map((o) => (
            <div
              key={o.id}
              className={`cs-opt${String(o.id) === String(value) ? " active" : ""}`}
              onMouseDown={() => select(o.id)}
            >
              {o.name}
            </div>
          ))}
          {canCreate && (
            <div className="cs-opt cs-create" onMouseDown={create}>
              {creating ? "Creating…" : `+ Create “${trimmed}”`}
            </div>
          )}
          {createErr && <div className="cs-err">{createErr}</div>}
        </div>
      )}
    </div>
  );
}
